/**
 * The patient-detail sub-nav: which tabs exist, which module owns each, and
 * which of them a given account may see.
 *
 * Split out of PatientDetailPage.tsx so the gating rules can be asserted
 * directly. Importing that page in a test transitively boots the app (its
 * store graph reaches src/main.tsx, which calls createRoot), so the rules were
 * previously only reachable through a full render of a 3,000-line component.
 */
import {
  Stethoscope,
  Beaker,
  BookOpen,
  Calendar,
  CheckCircle,
  CheckSquare,
  Clipboard,
  CreditCard,
  Crown,
  Dna,
  FileText,
  HardDrive,
  Heart,
  HeartPulse,
  MessageSquare,
  Package,
  Pill,
  Receipt,
  ScanLine,
  Share2,
  ShoppingBag,
  StickyNote,
  Syringe,
  Users,
} from 'lucide-react';
import type { SubNavSection } from '@/components/layout/DetailSubNav';

export const SUB_SECTIONS: SubNavSection[] = [
  { key: 'demographics', label: 'Details', icon: Users },
  { key: 'health', label: 'Health Information', icon: Heart },
  { key: 'medications', label: 'Medications', icon: Pill },
  // { key: 'financing', label: 'Financing Information', icon: CreditCard }, // temporarily hidden
  { key: 'plans', label: 'Plans', icon: BookOpen },
  { key: 'surgery', label: 'Surgery', icon: Stethoscope },
  { key: 'post-ops', label: 'Post-ops', icon: Stethoscope },
  { key: 'healthnexus', label: 'Health Nexus', icon: HeartPulse },
  { key: 'tasks', label: 'Tasks', icon: CheckSquare },
  { key: 'appointments', label: 'Appointments', icon: Calendar },
  // Quotes sits between Appointments and Payments because that is where the
  // legacy chart puts it (html/api/v4/navigation-patient.php builds the tab
  // list in this order). Pinned by patientDetailSections.quotes-gate.test.ts.
  { key: 'quotes', label: 'Quotes', icon: Receipt },
  { key: 'payments', label: 'Payments / Billing', icon: CreditCard },
  { key: 'memberships', label: 'Memberships', icon: Crown },
  { key: 'packages', label: 'Packages', icon: Package },
  { key: 'items', label: 'Items', icon: ShoppingBag },
  { key: 'labs', label: 'Labs', icon: Beaker },
  { key: 'imaging', label: 'Scans/Other', icon: ScanLine },
  { key: 'genetics', label: 'Genetics', icon: Dna },
  // Prescriptions, Partell Rx and Vitaboom share the pill icon and sit
  // together; Peptides (syringe) follows them.
  { key: 'prescriptions', label: 'Prescriptions', icon: Pill },
  { key: 'partell', label: 'Partell Rx', icon: Pill },
  { key: 'vitamins', label: 'Vitaboom', icon: Pill },
  { key: 'peptides', label: 'Peptides', icon: Syringe },
  { key: 'files', label: 'Files', icon: HardDrive },
  { key: 'referrals', label: 'Referrals', icon: Share2 },
  { key: 'commlog', label: 'Comm Log', icon: MessageSquare },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'forms', label: 'Forms', icon: Clipboard },
  { key: 'qa', label: 'Quality Assurance', icon: CheckCircle },
  { key: 'notes', label: 'Notes', icon: StickyNote },
];

export const DEFAULT_SECTION = 'demographics';

/**
 * SCRUM-1450 — strtolower(trim(modules.moduleTitle)) for the Health Nexus
 * catalog row (api/database/2026_08_14_health_nexus_module.sql). Health Nexus
 * is opt-in, so it is gated on the fail-CLOSED `explicit` signal rather than
 * SECTION_MODULE_KEYS below, which cannot express default-OFF. The server-side
 * halves of the same contract are HealthNexusAccessController::MODULE_KEY and
 * CommLogController::HEALTH_NEXUS_MODULE_KEY (plus the HealthNexus AI proxy,
 * once that controller lands from its own PR); ModuleGate matches the lowered
 * title exactly, so all of them must agree.
 */
export const HEALTH_NEXUS_MODULE_KEY = 'health nexus';

/**
 * strtolower(trim(modules.moduleTitle)) for the Quotes catalog row
 * (api/database/2026_09_15_quotes_module.sql). Quotes is opt-in while the V5
 * port runs alongside the legacy pageID=20 screen, so it is gated on the
 * fail-CLOSED `explicit` signal rather than SECTION_MODULE_KEYS below, which
 * cannot express default-OFF. The server half of the same contract is
 * QuotesController::MODULE_KEY; ModuleGate matches the lowered title exactly,
 * so the three must agree.
 */
export const QUOTES_MODULE_KEY = 'quotes';

/**
 * strtolower(trim(modules.moduleTitle)) for the Surgery catalog row
 * (api/database/2026_09_15_surgery_module.sql).
 *
 * Surgery is opt-in, so like Health Nexus it is gated on the fail-CLOSED
 * `explicit` signal rather than SECTION_MODULE_KEYS below, which cannot express
 * default-OFF. In legacy this tab had no account switch at all -- it was a
 * hardcoded `$accountID` allowlist in PHP -- so every account must be enabled
 * deliberately.
 *
 * A SECOND, independent gate also applies: legacy permission 6
 * (PERMISSION.SURGERY). The module says the practice has Surgery; the
 * permission says this user may open it. Both must pass, which is why
 * `surgeryEnabled` below is an AND rather than either one alone.
 *
 * The server half is SurgeryController::MODULE_KEY / PERMISSION_SURGERY.
 * ModuleGate matches the lowered title exactly, so the strings must agree.
 */
export const SURGERY_MODULE_KEY = 'surgery';

/**
 * strtolower(trim(modules.moduleTitle)) for the Post Ops catalog row
 * (api/database/2026_09_17_postops_module.sql). Post-ops is opt-in, so it is
 * gated on the fail-CLOSED `explicit` signal rather than SECTION_MODULE_KEYS
 * below, which cannot express default-OFF. The server-side halves of the same
 * contract are PostOpsController::MODULE_KEY and PERMISSION_POST_OPS;
 * ModuleGate matches the lowered title exactly, so all of them must agree.
 */
export const POST_OPS_MODULE_KEY = 'post ops';

/**
 * The module behind the Plans tab (see SECTION_MODULE_KEYS). Legacy's quote
 * screens show the Plan ID column, Find Plan and the "Plan # N" header only
 * when the Plans module (legacy module 1) is on, so they read this key too.
 */
export const PLANS_MODULE_KEY = 'crm';

/**
 * Which per-account module owns each tab (module disabled → tab hidden).
 * Unlisted keys (demographics) are core and always visible; `vitamins` and
 * `healthnexus` keep their bespoke fail-closed opt-in gates; `qa` additionally
 * needs the QA permission. Fail-open like the rest of the module map: a module
 * with no rows for the account never hides anything.
 */
export const SECTION_MODULE_KEYS: Record<string, string> = {
  health: 'clinical',
  medications: 'clinical',
  prescriptions: 'clinical',
  files: 'clinical',
  notes: 'clinical',
  documents: 'clinical',
  plans: PLANS_MODULE_KEY,
  referrals: 'crm',
  tasks: 'tasks',
  appointments: 'scheduling',
  payments: 'payments',
  memberships: 'memberships',
  packages: 'packages',
  items: 'inventory',
  labs: 'labs',
  imaging: 'labs',
  genetics: 'labs',
  commlog: 'messaging',
  forms: 'forms',
  qa: 'qa',
  peptides: 'axiom',
  partell: 'partell',
};

/**
 * The `Patients` module's key, and the slugs Corp-App can uncheck under it
 * (MODULE_SECTIONS.patients in AdminModuleSettings.jsx — the slugs are the
 * contract, so renaming one there silently un-hides the surface here).
 *
 * These sections are NOT routes. Patients has no sub-pages; the slugs name
 * surfaces inside the patient detail page, which is why they are matched by
 * name here instead of by the nav's path-segment matcher.
 */
export const PATIENTS_MODULE_KEY = 'patients';
export const PATIENTS_SECTION_APP_STATUS = 'app-status';
export const PATIENTS_SECTION_MEMBERSHIPS = 'memberships';

/**
 * Which patient-detail tabs a `patients` section slug hides.
 *
 * NOTE the deliberate overlap: `memberships` is ALSO in SECTION_MODULE_KEYS
 * above, pointing at the standalone `memberships` module. Both gates are real
 * and independent — the module answers "does this practice have memberships at
 * all", the section answers "does this practice want the tab on the patient
 * page" — so EITHER hides the tab. Neither is redundant; do not collapse them.
 */
const PATIENTS_SECTION_SECTIONS: Record<string, string> = {
  [PATIENTS_SECTION_MEMBERSHIPS]: 'memberships',
};

export interface SectionAccess {
  hasQaAccess: boolean;
  vitaminsEnabled: boolean;
  healthNexusEnabled: boolean;
  /**
   * Surgery is opt-in AND permission-gated, so this is the AND of an
   * explicitly-active `surgery` module row and legacy permission 6. Callers
   * combine both because either one alone is not authority to show the tab.
   */
  surgeryEnabled: boolean;
  /**
   * Quotes is opt-in. Optional so existing callers keep compiling, but absent
   * means HIDDEN — unlike `disabledPatientsSections` below, whose absence means
   * "nothing hidden". An opt-in gate that defaulted open would ship V5 Quotes
   * to every account the moment this tab existed.
   */
  quotesEnabled?: boolean;
  /**
   * Already the AND of: the `post ops` module being explicitly active for
   * this account, and this user holding legacy permission 7. Computed by the
   * caller because both halves live there.
   */
  postOpsEnabled: boolean;
  /**
   * The signed-in user's level. Legacy lists Quotes only for level above 1
   * (api/v4.2/navigation-patient.php:80). Absent means hidden.
   */
  userLevel?: number;
  /**
   * The practice's appointments add-on (aa_accounts_addons.appointments).
   * Legacy lists Quotes only inside the add-on's block
   * (api/v4.2/navigation-patient.php:77). Absent means hidden, as legacy
   * treats a practice with no add-on row.
   */
  appointmentsAddonEnabled?: boolean;
  disabledModuleKeys: Set<string>;
  /**
   * `disabled_sections` on the `patients` module for this account. Optional so
   * existing callers/tests keep compiling; absent means nothing is hidden,
   * which is the same fail-open default the module map uses.
   */
  disabledPatientsSections?: Set<string>;
}

/**
 * Which sub-nav tabs this account may see. Two gate directions live here on
 * purpose:
 *  - `vitamins`, `healthnexus`, `surgery` and `quotes` are OPT-IN, so they need
 *    a positive signal and stay hidden while loading, on error, and for an
 *    account with no rows;
 *  - everything in SECTION_MODULE_KEYS is opt-OUT, hidden only once the API has
 *    positively reported the owning module disabled.
 *
 * A tab can be claimed by both a module and a `patients` section (memberships
 * is), in which case either one hides it — see PATIENTS_SECTION_SECTIONS.
 */
export function computeVisibleSections(access: SectionAccess): SubNavSection[] {
  const hiddenByPatientsSection = new Set(
    Object.entries(PATIENTS_SECTION_SECTIONS)
      .filter(([slug]) => access.disabledPatientsSections?.has(slug))
      .map(([, sectionKey]) => sectionKey),
  );

  return SUB_SECTIONS.filter((s) => {
    if (hiddenByPatientsSection.has(s.key)) return false;
    if (s.key === 'qa') return access.hasQaAccess && !access.disabledModuleKeys.has('qa');
    if (s.key === 'vitamins') return access.vitaminsEnabled;
    if (s.key === 'healthnexus') return access.healthNexusEnabled;
    if (s.key === 'surgery') return access.surgeryEnabled;
    if (s.key === 'quotes') {
      return access.quotesEnabled === true
        && access.appointmentsAddonEnabled === true
        && (access.userLevel ?? 0) > 1;
    }
    if (s.key === 'post-ops') return access.postOpsEnabled;
    const moduleKey = SECTION_MODULE_KEYS[s.key];
    return !moduleKey || !access.disabledModuleKeys.has(moduleKey);
  });
}

/**
 * Deep-link guard: a `?section=` pointing at a tab this account cannot see
 * falls back to the default section rather than rendering an empty tab.
 */
export function resolveEffectiveSection(
  visibleSections: SubNavSection[],
  activeSection: string,
): string {
  return visibleSections.some((s) => s.key === activeSection) ? activeSection : DEFAULT_SECTION;
}
